/**
 * CustomerImportanceBadge · 2026-10-27
 *
 * Displays the AI-assigned importance rating (1-5 stars) for a customer along
 * with the human-readable tier label and (optionally) the customer-type pill.
 * Rated by the Gemini 3.1 Pro business-card analyser — not manually editable.
 *
 * Props:
 *   level        — number 0-5 (0 = not rated)
 *   customerType — "chain" | "importer" | "retailer" | "wholesaler" | "agent" | "unknown"
 *   compact      — if true, render the inline list-row variant (no reason/tier text)
 */
import { Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const TIER_COLORS = {
  5: "text-amber-500",       // 核心客户
  4: "text-orange-500",      // 重点客户
  3: "text-emerald-500",     // 潜力客户
  2: "text-slate-500",       // 普通客户
  1: "text-slate-400",       // 待观察
};

const TYPE_COLORS = {
  chain: "bg-amber-100 text-amber-700 border-amber-300",
  importer: "bg-blue-100 text-blue-700 border-blue-300",
  retailer: "bg-emerald-100 text-emerald-700 border-emerald-300",
  wholesaler: "bg-violet-100 text-violet-700 border-violet-300",
  agent: "bg-slate-100 text-slate-700 border-slate-300",
  unknown: "bg-slate-50 text-slate-500 border-slate-200",
};

export default function CustomerImportanceBadge({ level = 0, customerType = "", compact = false }) {
  const { t } = useLanguage();
  const lv = Math.max(0, Math.min(5, Number(level) || 0));
  if (lv === 0 && !customerType) {
    return (
      <span className="text-[11px] text-muted-foreground italic" data-testid="customer-importance-unrated">
        {t("customers.notRated") || "未评级"}
      </span>
    );
  }
  const color = TIER_COLORS[lv] || "text-slate-300";
  const tierLabel = lv > 0 ? t(`importance.${lv}`) : "";
  const typeKey = (customerType || "unknown").toLowerCase();
  const typeLabel = t(`customerType.${typeKey}`) || typeKey;

  return (
    <span className="inline-flex items-center gap-1.5 text-xs" data-testid={`customer-importance-badge-${lv}`}>
      <span className={`inline-flex ${color}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={compact ? 11 : 13}
            fill={n <= lv ? "currentColor" : "none"}
            strokeWidth={1.5}
          />
        ))}
      </span>
      {!compact && lv > 0 && (
        <span className="font-medium text-[11px] text-foreground/80">{tierLabel}</span>
      )}
      {customerType && typeKey !== "unknown" && (
        <span
          className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium ${TYPE_COLORS[typeKey] || TYPE_COLORS.unknown}`}
        >
          {typeLabel}
        </span>
      )}
    </span>
  );
}
